import { Box, Button, Flex, Tooltip, useDisclosure, useMediaQuery } from '@televet/kibble-ui';
import React, { ReactElement, useContext, useMemo } from 'react';
import styled from 'styled-components/macro';
import DeleteFormModal from '../components/DeleteFormModal';
import { FormsContext } from '../context/FormsContext';
import useForms from '../hooks/useForms';
import getFormDeleteDisabledMessages, { getComponentDisabledProps } from '../utils/getFormDeleteDisabledMessages';
import { FormViews } from './enums/FormViews';

interface IViewControl {
  toggleView: () => void;
  currentView: string;
}

const ViewControl = ({ currentView, toggleView }: IViewControl): ReactElement => {
  const { isActive, formId, formName, dispatch, formTemplateType, clinicWidgetRequestTypes, hasLinkedAutomations } =
    useContext(FormsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { toggleActive } = useForms();
  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');

  const handleToggleActive = (): void => {
    toggleActive(formId, !isActive);
    dispatch({
      type: 'RESET_NEW_FORM',
      payload: {},
    });
  };

  const disabledMessages = useMemo(() => {
    return getFormDeleteDisabledMessages({
      formTemplateType,
      clinicWidgetRequestTypes,
      hasLinkedAutomations,
      includeReason: true,
    });
  }, [formTemplateType, clinicWidgetRequestTypes, hasLinkedAutomations]);

  return (
    <>
      <DeleteFormModal isOpen={isOpen} onClose={onClose} formTemplateId={formId} formTemplateName={formName} />
      <Flex h="54px" w="100%" zIndex={1} boxShadow="base" position="relative">
        <Flex
          h="26px"
          w="154px"
          my={4}
          mx={isLargerThan1024 ? 'auto' : '15px'}
          border="1px"
          borderColor="border.default"
          borderRadius={6}
        >
          <Option
            isActive={currentView === FormViews.Compose}
            formView={FormViews.Compose}
            onClick={(): void => {
              toggleView();
            }}
            data-mixpanel-name="Compose form button"
            data-testid="compose-form-button"
          >
            Compose
          </Option>
          <Option
            isActive={currentView === FormViews.Preview}
            formView={FormViews.Preview}
            onClick={(): void => {
              toggleView();
            }}
            data-mixpanel-name="Preview form button"
            data-testid="preview-form-button"
          >
            Preview
          </Option>
        </Flex>
        <Flex mr="20px" h="100%" gap={2} align="center" position="absolute" right={0}>
          <Tooltip label={disabledMessages.delete} titleCaseLabel={false}>
            <Box>
              <Button
                isDisabled={Boolean(disabledMessages.delete)}
                size="sm"
                variant="ghostDestructive"
                onClick={onOpen}
              >
                Delete
              </Button>
            </Box>
          </Tooltip>
          <Tooltip
            label={getComponentDisabledProps({ isActive, disabledMessages }).disabledMessage}
            titleCaseLabel={false}
          >
            <Box>
              <Button
                isDisabled={getComponentDisabledProps({ isActive, disabledMessages }).isDisabled}
                size="sm"
                variant={isActive ? 'tertiary' : 'primary'}
                onClick={handleToggleActive}
              >
                {isActive ? 'Archive' : 'Unarchive'}
              </Button>
            </Box>
          </Tooltip>
        </Flex>
      </Flex>
    </>
  );
};

export default ViewControl;

interface IOptionProps {
  isActive: boolean;
  formView: FormViews;
}

const Option = styled.button<IOptionProps>`
  background-color: ${({ isActive }: IOptionProps): string => (isActive ? '#e0f1f4' : '#fff')};
  border-bottom-left-radius: ${({ formView }: IOptionProps): string => (formView === FormViews.Preview ? '0' : '6px')};
  border-bottom-right-radius: ${({ formView }: IOptionProps): string => (formView === FormViews.Compose ? '0' : '6px')};
  border-top-left-radius: ${({ formView }: IOptionProps): string => (formView === FormViews.Preview ? '0' : '6px')};
  border-top-right-radius: ${({ formView }: IOptionProps): string => (formView === FormViews.Compose ? '0' : '6px')};
  border: none;
  color: ${({ isActive }: IOptionProps): string => (isActive ? 'hsl(190deg 48% 34%)' : 'hsl(187deg 4% 48%)')};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  text-align: center;
  user-select: none;
  width: 100px;
`;
